import React, { createContext, useCallback, useContext, useRef, useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

interface ConfirmOptions {
  title: string;
  message?: React.ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  /** Red confirm button, for deletes and disconnects */
  destructive?: boolean;
}

interface PromptOptions extends ConfirmOptions {
  defaultValue?: string;
  placeholder?: string;
}

interface ConfirmContextType {
  /** In-app replacement for window.confirm: resolves true when the user confirms */
  confirm: (options: ConfirmOptions) => Promise<boolean>;
  /** In-app replacement for window.prompt: resolves the entered text, or null when cancelled */
  prompt: (options: PromptOptions) => Promise<string | null>;
}

type Pending =
  | { kind: "confirm"; options: ConfirmOptions; resolve: (v: boolean) => void }
  | { kind: "prompt"; options: PromptOptions; resolve: (v: string | null) => void };

const ConfirmContext = createContext<ConfirmContextType | undefined>(undefined);

export function ConfirmProvider({ children }: { children: React.ReactNode }) {
  const [pending, setPending] = useState<Pending | null>(null);
  const [value, setValue] = useState("");
  // Settles the open request exactly once, whichever way the dialog closes
  const settled = useRef(false);

  const confirm = useCallback(
    (options: ConfirmOptions) =>
      new Promise<boolean>((resolve) => {
        settled.current = false;
        setPending({ kind: "confirm", options, resolve });
      }),
    [],
  );

  const prompt = useCallback(
    (options: PromptOptions) =>
      new Promise<string | null>((resolve) => {
        settled.current = false;
        setValue(options.defaultValue ?? "");
        setPending({ kind: "prompt", options, resolve });
      }),
    [],
  );

  const finish = (ok: boolean) => {
    if (!pending || settled.current) return;
    settled.current = true;
    if (pending.kind === "confirm") pending.resolve(ok);
    else pending.resolve(ok ? value : null);
    setPending(null);
  };

  const o = pending?.options;

  return (
    <ConfirmContext.Provider value={{ confirm, prompt }}>
      {children}
      <Dialog open={!!pending} onOpenChange={(open) => !open && finish(false)}>
        <DialogContent className="sm:max-w-md md:max-w-md">
          <form
            onSubmit={(e) => {
              e.preventDefault();
              finish(true);
            }}
            className="space-y-5"
          >
            <DialogHeader>
              <DialogTitle>{o?.title}</DialogTitle>
              {o?.message && <DialogDescription>{o.message}</DialogDescription>}
            </DialogHeader>
            {pending?.kind === "prompt" && (
              <Input
                autoFocus
                value={value}
                placeholder={(o as PromptOptions).placeholder}
                onChange={(e) => setValue(e.target.value)}
              />
            )}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => finish(false)}>
                {o?.cancelLabel || "Cancel"}
              </Button>
              <Button type="submit" variant={o?.destructive ? "destructive" : "default"} autoFocus={pending?.kind === "confirm"}>
                {o?.confirmLabel || "OK"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </ConfirmContext.Provider>
  );
}

export function useConfirm() {
  const context = useContext(ConfirmContext);
  if (!context) {
    throw new Error("useConfirm must be used within a ConfirmProvider");
  }
  return context;
}
